import { SidebarSectionType } from 'data/types';

export function isSectionComplete(section: SidebarSectionType): boolean {
  if (section.requirementsMet !== undefined) {
    return section.requirementsMet;
  }

  if (section.required) {
    return (
      section.steps?.every(
        (step) => step.status === 'complete' || step.status === 'optional'
      ) ?? true
    );
  }

  return true;
}

export function getStepNumber(
  sections: SidebarSectionType[],
  currentSectionId: string
): number | undefined {
  const visibleSections = sections.filter(
    (section) => section.requirementsMet !== undefined
  );

  let stepNumber = 0;
  let previousGroup: string | undefined;

  for (const [index, section] of visibleSections.entries()) {
    const { progressBarGroup } = section;
    const continuesPreviousGroup =
      index > 0 &&
      progressBarGroup !== undefined &&
      progressBarGroup === previousGroup;

    if (!continuesPreviousGroup) {
      stepNumber += 1;
    }

    if (section.id === currentSectionId) {
      return stepNumber;
    }

    previousGroup = progressBarGroup;
  }

  return undefined;
}

type GetNextButtonTextParams = {
  nextButtonText: string | undefined;
  isLastSection: boolean;
};

export function getNextButtonText(params: GetNextButtonTextParams): string {
  const { nextButtonText, isLastSection } = params;
  if (nextButtonText) return nextButtonText;
  if (isLastSection) return 'Next Section';
  return 'Next';
}

export function isFooterAtLastSection(
  viewOnly: boolean,
  currentIndex: number,
  sectionCount: number
): boolean {
  return viewOnly || currentIndex === sectionCount - 1;
}

export type StepActionButtonConfig = {
  text: string;
  onClick: () => void;
  disabled?: boolean;
  disabledReason?: string;
};

type GetAccordionNextButtonTextParams = {
  nextButtonText: string | undefined;
  nextStepLabel: string | undefined;
  isLastSection: boolean;
};

export function getAccordionNextButtonText(
  params: GetAccordionNextButtonTextParams
): string {
  const { nextButtonText, nextStepLabel, isLastSection } = params;
  if (nextButtonText) return nextButtonText;
  if (!isLastSection && nextStepLabel) return `Next: ${nextStepLabel}`;
  return getNextButtonText({ nextButtonText, isLastSection });
}

export function getAccordionCircleClasses(
  isComplete: boolean,
  isActive: boolean,
  isEnabled: boolean
): string {
  if (isComplete) return 'border-green-600 bg-green-600 text-white';
  if (isActive) return 'border-green-600 bg-white text-green-700';
  if (isEnabled) return 'border-gray-300 bg-white text-gray-500';
  return 'border-gray-200 bg-gray-50 text-gray-400';
}

export function getAccordionCollapsedRowClasses(
  isInteractive: boolean,
  readOnly: boolean
): string {
  if (isInteractive)
    return 'cursor-pointer hover:border-gray-300 hover:bg-white';
  if (readOnly) return 'cursor-default';
  return 'cursor-not-allowed opacity-60';
}

export function getStepSidebarPanelColumnClassName(
  panelVariant: 'tabs' | 'accordion'
): string {
  return panelVariant === 'accordion'
    ? 'flex h-full min-h-0 shrink-0 flex-col gap-4'
    : 'flex shrink-0 flex-col gap-4';
}

type GetDisabledReasonParams = {
  canGoNext: boolean;
  nextButtonDisabledReason: string | undefined;
  currentSectionRequirementsMet: boolean;
  canMarkComplete: boolean;
  isLastSection: boolean;
  nextSectionDisabled: boolean;
  nextSectionDisabledReason: string | undefined;
  allRequiredPreviousSectionsComplete: boolean;
  currentSectionConfig: SidebarSectionType | undefined;
  initialSections: SidebarSectionType[];
  currentSectionId: string;
};

export function getDisabledReason(
  params: GetDisabledReasonParams
): string | undefined {
  const {
    canGoNext,
    nextButtonDisabledReason,
    currentSectionRequirementsMet,
    canMarkComplete,
    isLastSection,
    nextSectionDisabled,
    nextSectionDisabledReason,
    allRequiredPreviousSectionsComplete,
    currentSectionConfig,
    initialSections,
    currentSectionId
  } = params;

  if (canGoNext) return undefined;
  if (nextButtonDisabledReason) return nextButtonDisabledReason;

  const sectionFallback =
    currentSectionConfig?.requirementsMissingReason ??
    currentSectionConfig?.description;

  if (!currentSectionRequirementsMet) return sectionFallback;
  if (!canMarkComplete) return sectionFallback;
  if (isLastSection && nextSectionDisabled) {
    return nextSectionDisabledReason ?? sectionFallback;
  }
  if (isLastSection && !allRequiredPreviousSectionsComplete) {
    const incompleteRequiredPrevious = initialSections
      .filter(
        (s) =>
          s.required !== false &&
          s.id !== currentSectionId &&
          s.requirementsMet !== true
      )
      .map((s) => s.progressBarLabel ?? s.title);
    if (incompleteRequiredPrevious.length > 0) {
      return `Complete these steps first: ${incompleteRequiredPrevious.join(
        ', '
      )}`;
    }
    return currentSectionConfig?.description;
  }
  return undefined;
}
